import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Download, FileText, Loader2, ShieldCheck, X } from "lucide-react";

/* The agent's own half of a form, and the record of what a client signed.

   Two small screens that were missing rather than broken: the endpoint that
   stamps a form for a client has existed since fields could be placed, but
   nothing on the seller's Forms tab ever called it, so a form with fields on
   it still only offered a blank download. */

const muted = { color: "var(--text-muted)" };

const shell = (children, onClose) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 940, background: "rgba(28,25,23,0.55)",
                display: "flex", alignItems: "flex-start", justifyContent: "center",
                padding: 16, overflowY: "auto" }}
       onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="card" style={{ background: "#fff", padding: 26, maxWidth: 560, width: "100%",
                                   marginTop: 40 }}>
      {children}
    </div>
  </div>
);

/* ----------------------------------------------------- the agent fills in */

export function FillForm({ sellerId, template, onClose }) {
  const [fields, setFields] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/form-library/${template.template_id}/fields`);
        if (alive) setFields(data.fields || []);
      } catch (e) {
        if (alive) setErr(formatApiError(e?.response?.data?.detail) || "Couldn't open that form.");
      }
    })();
    return () => { alive = false; };
  }, [template.template_id]);

  // Fields with no `who` were placed before that existed, on forms the client
  // fills -- so only ones explicitly marked for the agent land here.
  const mine = (fields || []).filter(
    (f) => f.who === "agent"
      && (f.source || {}).type === "ask"
      && !["signature", "initial"].includes(f.kind)
  ).sort((a, b) => a.page - b.page || b.rect.y - a.rect.y || a.rect.x - b.rect.x);

  const theirs = (fields || []).filter((f) => (f.who || "seller") === "seller").length;
  const auto = (fields || []).filter((f) => (f.source || {}).type === "prefill").length;

  const build = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/admin/seller/${sellerId}/form/${template.template_id}/fill`,
        { answers }, { responseType: "blob", timeout: 120000 });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `${template.name || "form"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    } catch (e) {
      // A blob response type means an error body arrives as a Blob too, so the
      // usual e.response.data.detail is not a string here -- read it back.
      let detail = "";
      try { detail = JSON.parse(await e?.response?.data?.text?.() || "{}").detail || ""; } catch { /* keep going */ }
      setErr(formatApiError(detail) || detail || "Couldn't build that PDF.");
    } finally { setBusy(false); }
  };

  return shell(
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div className="overline">Fill in</div>
          <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 0" }}>
            {template.name}
          </h2>
        </div>
        <button type="button" className="btn-ghost" style={{ padding: 6 }} onClick={onClose}
                data-testid="fill-close" title="Close"><X size={18} /></button>
      </div>

      {!fields ? (
        <p style={{ ...muted, display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
          <Loader2 size={14} /> Loading&hellip;
        </p>
      ) : (
        <>
          <p style={{ ...muted, fontSize: 13.5, marginTop: 10 }}>
            {auto > 0 && <>{auto} field{auto === 1 ? "" : "s"} fill themselves from this client&rsquo;s record. </>}
            {theirs > 0 && <>{theirs} {theirs === 1 ? "is" : "are"} for the client to fill and sign. </>}
            {mine.length === 0
              ? "Nothing on this one is marked as yours yet \u2014 open Form library, "
                + "choose this form, click Place fields, select a field and set "
                + "\u201cWho fills this in\u201d to You."
              : `${mine.length} ${mine.length === 1 ? "is" : "are"} yours.`}
          </p>

          {mine.map((f) => (
            <div key={f.field_id} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, ...muted }}>
                {f.label || "Answer"} <span style={{ opacity: 0.6 }}>&middot; p{f.page + 1}</span>
              </label>
              {f.kind === "check" ? (
                <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5,
                                marginTop: 4, cursor: "pointer" }}>
                  <input type="checkbox" data-testid={`fill-${f.field_id}`}
                         checked={!!answers[f.field_id]}
                         onChange={(e) => setAnswers((a) => ({
                           ...a, [f.field_id]: e.target.checked ? "yes" : "" }))} />
                  Yes
                </label>
              ) : f.kind === "multiline" ? (
                <textarea className="input-field" rows={3} data-testid={`fill-${f.field_id}`}
                          value={answers[f.field_id] || ""} maxLength={300}
                          onChange={(e) => setAnswers((a) => ({ ...a, [f.field_id]: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", fontSize: 13.5 }} />
              ) : (
                <input className="input-field" data-testid={`fill-${f.field_id}`}
                       value={answers[f.field_id] || ""} maxLength={300}
                       onChange={(e) => setAnswers((a) => ({ ...a, [f.field_id]: e.target.value }))}
                       style={{ width: "100%", padding: "8px 10px", fontSize: 13.5 }} />
              )}
            </div>
          ))}

          {err && <p style={{ color: "var(--secondary)", fontSize: 13 }} data-testid="fill-error">{err}</p>}

          <button type="button" className="btn-primary" onClick={build} disabled={busy}
                  data-testid="fill-build"
                  style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 7 }}>
            <Download size={14} /> {busy ? "Building…" : "Build the PDF"}
          </button>
          <p style={{ ...muted, fontSize: 12, marginTop: 10, marginBottom: 0 }}>
            This downloads a copy for you. It doesn&rsquo;t collect the client&rsquo;s signature
            &mdash; they do that from their own Forms page.
          </p>
        </>
      )}
    </>, onClose);
}

/* ------------------------------------------------- what the client signed */

const ROWS = [
  ["Signed by", "signer_name"],
  ["Account email", "signer_email"],
  ["Typed as", "typed_name"],
  ["Initials", "typed_initials"],
  ["Boxes signed in place", "placed_count"],
  ["Signed at (UTC)", "signed_at"],
  ["Signature adopted", "adopted_at"],
  ["IP address", "ip"],
  ["Browser", "user_agent"],
  ["Field layout version", "field_version"],
  ["Blank form SHA-256", "template_sha256"],
  ["Signed document SHA-256", "document_sha256"],
];

export function AuditRecord({ signedFormId, onClose }) {
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/signed-form/${signedFormId}`);
        if (alive) setRec(data);
      } catch (e) {
        if (alive) setErr(formatApiError(e?.response?.data?.detail) || "Couldn't load that record.");
      }
    })();
    return () => { alive = false; };
  }, [signedFormId]);

  return shell(
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div className="overline">
            <ShieldCheck size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
            Signing record
          </div>
          <h2 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "6px 0 0" }}>
            {rec?.template_name || "Signed form"}
          </h2>
        </div>
        <button type="button" className="btn-ghost" style={{ padding: 6 }} onClick={onClose}
                data-testid="audit-close" title="Close"><X size={18} /></button>
      </div>

      {err && <p style={{ color: "var(--secondary)", fontSize: 13.5 }}>{err}</p>}
      {!rec && !err && <p style={{ ...muted, marginTop: 14 }}>Loading&hellip;</p>}

      {rec && (
        <>
          <div style={{ marginTop: 16 }} data-testid="audit-rows">
            {ROWS.map(([label, key]) => (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "150px minmax(0,1fr)",
                                      gap: 12, padding: "7px 0",
                                      borderTop: "1px solid var(--border)" }}>
                <div style={{ ...muted, fontSize: 10.5, letterSpacing: "0.07em",
                              textTransform: "uppercase", paddingTop: 2 }}>{label}</div>
                <div style={{ fontSize: 12.5, wordBreak: "break-all" }}>
                  {rec[key] || "—"}
                  {key === "ip" && rec[key] && (
                    <span style={{ ...muted }}> (self-reported by the browser)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p style={{ ...muted, fontSize: 12, marginTop: 14, lineHeight: 1.55 }}>
            The same details are printed on a certificate page at the end of the signed
            PDF. The document hash covers the signed pages, not the certificate itself.
          </p>
        </>
      )}
    </>, onClose);
}

export default FillForm;
